import React, { useEffect, useMemo, useRef, useState } from 'react';

export type PingColor = 'green' | 'orange' | 'red' | 'gray';
export type PingPhase = 'idle' | 'warming' | 'collecting' | 'ready' | 'offline';
export interface PingThresholds {
  warn: number;
  bad: number;
}

export interface PingIndicatorProps {
  url?: string;
  /** интервал между замерами в установившемся режиме */
  intervalMs?: number;
  /** интервал между замерами во время стартовой серии */
  burstDelayMs?: number;
  /** слать ли прогревочный запрос */
  warmup?: boolean;
  /** пауза после прогрева перед первым честным замером */
  settleMs?: number;
  /** размер скользящего окна для медианы */
  windowSize?: number;
  /** сколько успешных замеров нужно, чтобы начать показывать число */
  minSamples?: number;
  timeoutMs?: number;
  /** сколько подряд ошибок, чтобы объявить offline */
  maxFailures?: number;
  /** ресурс для пинга. Для чистых бэкендов без статики используйте '/' или '/health' */
  probePath?: string;
  thresholds?: PingThresholds;
  /** округление отображаемого числа, чтобы цифры не дрыгались */
  roundTo?: number;
  /** после какого простоя сбрасывать окно и греть заново */
  idleResetMs?: number;
  className?: string;
}

/* ============================== utils ============================== */

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** Медиана устойчива к одиночным выбросам — в отличие от среднего. */
const median = (v: number[]): number => {
  if (!v.length) return 0;
  const s = [...v].sort((a, b) => a - b);
  const m = s.length >> 1;
  return s.length % 2 ? s[m] : Math.round((s[m - 1] + s[m]) / 2);
};

const toOrigin = (url: string): string | null => {
  try {
    const u = new URL(/^[a-z][a-z\d+.-]*:\/\//i.test(url) ? url : `https://${url}`);
    return u.origin;
  } catch {
    return null;
  }
};

const probe = (
  origin: string,
  path: string,
  timeoutMs: number,
  outer: AbortSignal
): Promise<number | null> => {
  return new Promise((resolve) => {
    if (outer.aborted) return resolve(null);

    const img = new Image();
    const t0 = performance.now();
    let settled = false;

    const done = (ms: number | null) => {
      if (settled) return;
      settled = true;
      img.src = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
      resolve(ms);
    };

    const timer = setTimeout(() => done(null), timeoutMs);
    const onAbort = () => done(null);
    outer.addEventListener('abort', onAbort, { once: true });

    img.onload = img.onerror = () => {
      clearTimeout(timer);
      outer.removeEventListener('abort', onAbort);
      done(Math.round(performance.now() - t0));
    };

    const url = `${origin}${path}${path.includes('?') ? '&' : '?'}_=${Date.now()}`;
    img.src = url;
  });
};

const warmCache = new Map<string, number>();
const WARM_TTL = 60_000;

const warmUp = async (
  origin: string,
  path: string,
  timeoutMs: number,
  outer: AbortSignal,
  force = false
) => {
  const key = origin + path;
  if (!force && Date.now() - (warmCache.get(key) ?? 0) < WARM_TTL) return;
  const ms = await probe(origin, path, timeoutMs, outer); // результат выбрасываем
  if (ms !== null) warmCache.set(key, Date.now());
};

const colorFor = (ms: number, prev: PingColor, { warn, bad }: PingThresholds): PingColor => {
  const H = 10;
  if (ms >= bad) return 'red';
  if (ms >= warn) return prev === 'red' && ms >= bad - H ? 'red' : 'orange';
  return prev === 'orange' && ms >= warn - H ? 'orange' : 'green';
};

/* ============================ component ============================ */

const PingIndicator: React.FC<PingIndicatorProps> = ({
  url,
  intervalMs = 5000,
  burstDelayMs = 600,
  warmup = true,
  settleMs = 250,
  windowSize = 5,
  minSamples = 3,
  timeoutMs = 5000,
  maxFailures = 3,
  probePath = '/',
  thresholds = { warn: 300, bad: 500 },
  roundTo = 5,
  idleResetMs = 30_000,
  className = '',
}) => {
  const { warn, bad } = thresholds;
  const origin = useMemo(() => (url ? toOrigin(url) : null), [url]);

  const [phase, setPhase] = useState<PingPhase>('idle');
  const [latency, setLatency] = useState<number | null>(null);
  const [color, setColor] = useState<PingColor>('gray');
  const [history, setHistory] = useState<number[]>([]);

  const colorRef = useRef<PingColor>('gray');

  useEffect(() => {
    colorRef.current = 'gray';
    setPhase('idle');
    setLatency(null);
    setColor('gray');
    setHistory([]);

    if (!origin || typeof window === 'undefined') return;

    let stopped = false;
    let needWarm = warmup;
    let forceWarm = false;
    let hiddenAt = 0;
    let fails = 0;
    const samples: number[] = [];
    const ac = new AbortController();

    const publish = (p: PingPhase, ms: number | null, c: PingColor, h: number[]) => {
      if (stopped) return;
      colorRef.current = c;
      setPhase(p);
      setLatency(ms);
      setColor(c);
      setHistory(h);
    };

    const onVisibility = () => {
      if (document.hidden) {
        hiddenAt = Date.now();
        return;
      }

      if (Date.now() - hiddenAt > idleResetMs) {
        samples.length = 0;
        needWarm = warmup;
        forceWarm = true;
        publish('collecting', null, 'gray', []);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    (async () => {
      while (!stopped) {
        if (document.hidden) {
          await sleep(1000);
          continue;
        }

        if (needWarm) {
          publish('warming', null, 'gray', []);
          await warmUp(origin, probePath, timeoutMs, ac.signal, forceWarm);
          forceWarm = false;
          needWarm = false;
          if (settleMs) await sleep(settleMs);
          if (stopped) return;
        }

        const raw = await probe(origin, probePath, timeoutMs, ac.signal);
        if (stopped) return;

        if (raw === null) {
          if (++fails >= maxFailures) publish('offline', null, 'red', []);
        } else {
          fails = 0;
          samples.push(raw);
          while (samples.length > windowSize) samples.shift();

          const ready = samples.length >= minSamples;
          const smooth = median(samples);
          publish(
            ready ? 'ready' : 'collecting',
            ready ? smooth : null,
            ready ? colorFor(smooth, colorRef.current, { warn, bad }) : 'gray',
            [...samples]
          );
        }

        await sleep(samples.length < minSamples ? burstDelayMs : intervalMs);
      }
    })();

    return () => {
      stopped = true;
      ac.abort();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [
    origin,
    probePath,
    warmup,
    settleMs,
    intervalMs,
    burstDelayMs,
    windowSize,
    minSamples,
    timeoutMs,
    maxFailures,
    warn,
    bad,
    idleResetMs,
  ]);

  if (!url || !origin) return null;

  const shown = latency === null ? null : Math.round(latency / roundTo) * roundTo;
  const pending = phase === 'warming' || phase === 'collecting' || phase === 'idle';

  const title = history.length
    ? `Медиана: ${median(history)} мс\nмин/макс: ${Math.min(...history)}/${Math.max(...history)} мс\nзамеров: ${history.length}`
    : 'Измерение…';

  return (
    <div
      title={title}
      className={`bg-background-500 inline-flex items-center gap-2 px-1.5 py-0.5 text-sm font-medium ${className}`}
    >
      <span
        className={`size-1.5 shrink-0 rounded-full transition-colors duration-500 ${pending ? 'animate-pulse' : ''}`}
        style={{ backgroundColor: color }}
      />
      <span className="tabular-nums" style={{ color, fontWeight: 'normal', fontSize: 'x-small' }}>
        {shown === null ? '···' : `${shown} мс`}
      </span>
    </div>
  );
};

export default PingIndicator;
